import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Users, UserPlus, Mail, Trash2, Shield, ShieldOff, Loader2, X, CheckCircle2, AlertTriangle, UserCheck, UserX } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function AdminUsers() {
  const { user: current } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null); // "create"
  const [actionId, setActionId] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try { const { data } = await api.get("/admin/users"); setList(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u, patch) => {
    setActionId(u.id);
    try { await api.patch(`/admin/users/${u.id}`, patch); await load(); }
    catch (e) { setErr(formatErr(e)); }
    finally { setActionId(""); }
  };

  const resendInvite = async (u) => {
    setActionId(u.id);
    try { await api.post(`/admin/users/${u.id}/send-invite`); await load(); }
    catch (e) { setErr(formatErr(e)); }
    finally { setActionId(""); }
  };

  const del = async (u) => {
    if (!window.confirm(`Benutzer "${u.email}" und alle zugehörigen Daten endgültig löschen?`)) return;
    setActionId(u.id);
    try { await api.delete(`/admin/users/${u.id}`); await load(); }
    catch (e) { setErr(formatErr(e)); }
    finally { setActionId(""); }
  };

  const stats = {
    total: list.length,
    admins: list.filter(u => u.role === "admin").length,
    pending: list.filter(u => u.pending_invite).length,
    inactive: list.filter(u => u.active === false).length,
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3"><Users size={26} className="text-blue-400"/>Benutzerverwaltung</h1>
          <p className="text-slate-400 text-sm mt-1">Konten anlegen, Rollen vergeben und Einladungen senden.</p>
        </div>
        <button className="btn-amber" onClick={()=>setModal("create")} data-testid="open-create-user-button">
          <UserPlus size={16}/>Neuen Benutzer anlegen
        </button>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <Stat label="Benutzer gesamt" value={stats.total} testid="admin-stat-total"/>
        <Stat label="Administratoren" value={stats.admins} accent="text-blue-300" testid="admin-stat-admins"/>
        <Stat label="Ausstehende Einladungen" value={stats.pending} accent="text-amber-300" testid="admin-stat-pending"/>
        <Stat label="Deaktiviert" value={stats.inactive} accent="text-rose-300" testid="admin-stat-inactive"/>
      </div>

      {err && <div className="card border-rose-500/40 bg-rose-500/5 p-3 text-rose-200 text-sm flex gap-2" data-testid="admin-users-error">
        <AlertTriangle size={16} className="mt-0.5"/><span>{err}</span>
      </div>}

      <div className="card overflow-x-auto">
        {loading ? (
          <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin"/>Lade Benutzer...</div>
        ) : (
          <table className="tbl min-w-[900px]" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Firma</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u, i)=>(
                <tr key={u.id} data-testid={`admin-user-row-${i}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${u.role==="admin" ? "bg-blue-500/10 border-blue-500/30 text-blue-300" : "bg-slate-800/60 border-slate-700 text-slate-300"}`}>
                        {u.role === "admin" ? <Shield size={16}/> : <Users size={16}/>}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{u.name || "—"}</div>
                        <div className="text-xs font-mono text-slate-400 truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-sm text-slate-300 truncate max-w-[220px]">{u.company_name || "—"}</td>
                  <td>
                    {u.role === "admin"
                      ? <span className="chip chip-valid"><Shield size={11}/>Admin</span>
                      : <span className="chip chip-draft">User</span>}
                  </td>
                  <td>
                    {u.active === false
                      ? <span className="chip chip-error"><UserX size={11}/>Deaktiviert</span>
                      : u.pending_invite
                        ? <span className="chip chip-pending"><Mail size={11}/>Einladung offen</span>
                        : <span className="chip chip-valid"><UserCheck size={11}/>Aktiv</span>}
                  </td>
                  <td className="text-xs font-mono text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE") : "—"}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-2 flex-wrap justify-end">
                      <button className="btn-ghost" onClick={()=>resendInvite(u)} disabled={actionId===u.id} data-testid={`invite-btn-${i}`}>
                        <Mail size={13}/>Einladung senden
                      </button>
                      {u.role !== "admin" ? (
                        <button className="btn-ghost" onClick={()=>toggle(u,{role:"admin"})} disabled={actionId===u.id} data-testid={`promote-btn-${i}`}>
                          <Shield size={13}/>Zu Admin
                        </button>
                      ) : u.id !== current?.id ? (
                        <button className="btn-ghost" onClick={()=>toggle(u,{role:"user"})} disabled={actionId===u.id} data-testid={`demote-btn-${i}`}>
                          <ShieldOff size={13}/>Zu User
                        </button>
                      ) : null}
                      {u.id !== current?.id && (
                        <>
                          <button className="btn-ghost" onClick={()=>toggle(u,{active: u.active===false})} disabled={actionId===u.id} data-testid={`toggle-active-btn-${i}`}>
                            {u.active===false ? <><UserCheck size={13}/>Aktivieren</> : <><UserX size={13}/>Deaktivieren</>}
                          </button>
                          <button className="btn-danger" onClick={()=>del(u)} disabled={actionId===u.id} data-testid={`delete-user-btn-${i}`}>
                            <Trash2 size={13}/>Löschen
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal === "create" && <CreateUserDialog onClose={()=>setModal(null)} onSaved={async ()=>{ setModal(null); await load(); }}/>}
    </div>
  );
}

function CreateUserDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ email:"", name:"", company_name:"", role:"user", send_invite:true });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/admin/users", form);
      setDone(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4" data-testid="create-user-dialog">
      <div className="card w-full max-w-lg p-6 fade-in relative">
        <button className="absolute top-3 right-3 btn-ghost p-2" onClick={onClose} data-testid="close-create-dialog"><X size={16}/></button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <UserPlus size={18} className="text-amber-300"/>
          </div>
          <div>
            <h3 className="font-display text-xl font-bold">Neuen Benutzer anlegen</h3>
            <p className="text-xs text-slate-400 mt-0.5">Der Kunde erhält eine E-Mail und legt sein Passwort selbst fest.</p>
          </div>
        </div>

        {done ? (
          <div className="text-center py-6 space-y-3" data-testid="user-created-success">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 size={28} className="text-emerald-400"/>
            </div>
            <div className="font-display text-lg font-semibold">Benutzer angelegt</div>
            <div className="text-sm text-slate-300"><span className="font-mono">{done.email}</span></div>
            <div className="text-xs text-slate-400">
              {done.invite_sent ? "Einladungs-E-Mail wurde versendet." : "Einladung konnte nicht versendet werden – Sie können sie manuell erneut senden."}
            </div>
            <button className="btn-primary mt-2" onClick={onSaved} data-testid="close-after-create">Fertig</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="create-user-form">
            <div>
              <label className="label">E-Mail *</label>
              <input type="email" required className="input" value={form.email} onChange={onChange("email")} data-testid="new-user-email" placeholder="kunde@firma.de"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Name</label>
                <input className="input" value={form.name} onChange={onChange("name")} data-testid="new-user-name" placeholder="Max Mustermann"/>
              </div>
              <div>
                <label className="label">Firma</label>
                <input className="input" value={form.company_name} onChange={onChange("company_name")} data-testid="new-user-company" placeholder="KFZ Mustermann"/>
              </div>
            </div>
            <div>
              <label className="label">Rolle</label>
              <select className="select" value={form.role} onChange={onChange("role")} data-testid="new-user-role">
                <option value="user">User (nur eigene Rechnungen)</option>
                <option value="admin">Administrator (volle Rechte)</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.send_invite} onChange={onChange("send_invite")} className="accent-blue-500" data-testid="new-user-send-invite"/>
              Einladungs-E-Mail jetzt senden (empfohlen)
            </label>
            {err && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3" data-testid="create-user-error">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <button type="button" className="btn-ghost" onClick={onClose} data-testid="cancel-create-user">Abbrechen</button>
              <button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-create-user">
                {submitting ? <Loader2 size={16} className="animate-spin"/> : <UserPlus size={16}/>}
                {submitting ? "Lege an..." : "Benutzer anlegen"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const Stat = ({label, value, accent="text-slate-100", testid}) => (
  <div className="card p-4" data-testid={testid}>
    <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
  </div>
);
